import type { DriveDocuments } from './drive';

// Typed client for the Affilmaster Space reporting API (/api/v1/affilmaster/*).
// Read-only. The provider returns a readiness-gated envelope: when the required
// sync sources (partner_dims + pap_reporting) are not ok+fresh it responds
// { ok:false, error:'affilmaster_data_not_ready', freshness } with HTTP 200.
// We deliberately do NOT route this through the shared api client (which throws
// on ok:false) - not-ready is a first-class UI STATE (hard not-ready banner),
// never a thrown error and never silently coerced to zeros.

export interface FreshnessSource {
  source: string;
  status: string;
  last_success_at: string | null;
  last_success_age_hours: number | null;
  data_through_date: string | null;
  required: boolean;
  fresh: boolean;
}
export interface Readiness {
  ready: boolean;
  problems: string[];
  thresholds: { maxAgeHours: number; maxDataLagDays: number };
  sources: FreshnessSource[];
}

export interface AffiliateListItem {
  pap_affiliate_id: string;
  vip_partner?: boolean;
  display_name: string | null;
  company_name: string | null;
  website: string | null;
  country: string | null;
  markets: string | null;
  status: string | null;
  email: string | null;
  primary_contact_name: string | null;
  registrations: number;
  ftd_count: number;
  deposits_volume: number;
  ngr_volume: number;
  commission: number;
}

export interface AffiliateUdaje {
  source?: string | null;
  vip_partner?: boolean;
  crm_lead_id?: number | null;
  display_name: string | null;
  company_name: string | null;
  country: string | null;
  markets: string | null;
  status: string | null;
  website: string | null;
  email: string | null;
  contact_person: string | null;
  phone: string | null;
  vocative: string | null;
  first_name: string | null;
  last_name: string | null;
  ico: string | null;
  dic: string | null;
  street: string | null;
  city: string | null;
  zipcode: string | null;
  approved_at: string | null;
  last_activity_at: string | null;
  contact_override_name: string | null;
  billing_currency: string | null;
  payout_currency: string | null;
  bank_account_czk: string | null;
  bank_account_eur: string | null;
  invoice_language: string | null;
  vat_tariff: string | null;
  min_payout: number | null;
}

export interface AffiliateMonth {
  ym: string;
  campaigns: number;
  registrations: number;
  ftd_count: number;
  deposits_count: number;
  withdrawals_count: number;
  deposits_volume: number;
  ngr_volume: number;
  commission: number;
  commission_rs: number;
  commission_cpa: number;
  clicks?: number;
}
export interface AffiliateCampaign {
  campaign_id: string;
  campaign_name: string | null;
  is_vip?: boolean;
  registrations: number;
  ftd_count: number;
  deposits_volume: number;
  ngr_volume: number;
  commission: number;
  commission_rs: number;
  commission_cpa: number;
  clicks?: number;
  first_day: string | null;
  last_day: string | null;
}
export interface AffiliateMonthly {
  pap_affiliate_id: string;
  currency: string;
  affiliate: AffiliateUdaje | null;
  months: AffiliateMonth[];
  campaigns: AffiliateCampaign[];
  vip_accounts?: VipAccount[];
}

export interface VipAccount {
  id: number;
  instanceId: number;
  instanceCode: string;
  casino: string;
  papAffiliateId: string;
  cardName: string | null;
  localAffiliateId: string;
  referralId: string | null;
  campaignId: string;
  campaignKey: string;
  sourceName: string | null;
  companyInSource: string | null;
  websiteInSource: string | null;
  commissionGroupId: string | null;
  commissionGroupName: string | null;
  assignmentKind: string | null;
  membershipStatus: string | null;
  perSaleRate: number | null;
  otherCommissions: string | null;
  classification: string | null;
  historyNonzeroNet: boolean | null;
  sourceObservedAt: string | null;
  note: string | null;
  vipBilling: boolean;
}
export interface VipInstance {
  id: number; code: string; casino: string; origin: string; mainCampaignId: string;
  partnerId: number | null; partnerName: string | null; vipBilling: boolean;
  credentialsEnv: string; credentialsSource: 'env' | 'stored' | 'none'; credentialsUser: string | null; credentialsUpdatedAt: string | null;
  accessStatus: string; lastReadAt: string | null; lastError: string | null; accounts: number;
}
export interface VipCommissionGroup {
  instanceCode: string; casino: string; campaignId: string; campaignName: string | null; commissionGroupId: string;
  name: string | null; perSaleRate: number | null; commissionSummary: string | null; isDefault: boolean; explicitMembers: number | null; observedAt: string | null;
}
export interface VipOverview {
  canStoreCredentials: boolean;
  syncRunning: boolean;
  instances: VipInstance[];
  partners: Array<{ papAffiliateId: string; name: string | null; website: string | null; accounts: VipAccount[] }>;
  groups: VipCommissionGroup[];
  sync: Array<{ source: string; status: string | null; lastSuccessAt: string | null; lastError: string | null; dataThroughDate: string | null }>;
}
export const setAffilmasterVipCredentials = (instanceId: number, user: string, password: string) =>
  afPost<{ stored: boolean }>(`/api/v1/affilmaster/vip-pap/instances/${instanceId}/credentials`, { user, password });
export const runAffilmasterVipSync = (instance?: string) =>
  afPost<{ started: boolean; instance: string | null }>('/api/v1/affilmaster/vip-pap/sync', instance ? { instance } : {});
export async function getAffilmasterVipOverview(): Promise<VipOverview> {
  const { body } = await getRaw('/api/v1/affilmaster/vip-pap/overview');
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'vip overview vrátil non-ok');
  return body.data as VipOverview;
}

export type Reporting<T> =
  | { ok: true; data: T; freshness: Readiness }
  | { ok: false; notReady: true; freshness: Readiness };

export class AffilmasterApiError extends Error {
  constructor(public readonly status: number, public readonly code: string, message?: string) {
    super(message ?? `${status} ${code}`);
  }
}

async function getRaw<T = unknown>(path: string): Promise<{ status: number; body: any }> {
  const res = await fetch(path, { method: 'GET', credentials: 'include' });
  let body: any = null;
  try { body = await res.json(); } catch { /* empty */ }
  // Real transport/auth errors (401/403/5xx) ARE errors. A 200 with ok:false
  // (not-ready) is a valid reporting state, handled by the caller.
  if (!res.ok) {
    throw new AffilmasterApiError(res.status, body?.error?.code ?? body?.error ?? `HTTP_${res.status}`, body?.error?.message);
  }
  return { status: res.status, body };
}

export interface KlientListItem {
  id: number;
  name: string;
  company: string | null;
  website: string | null;
  market: string;
  logo: string | null;
  logoBg: string | null;
  isExclusive: boolean;
  currency: string;
  active: boolean;
  campaigns: number;
}
export interface KlientCampaign {
  rowId: number;
  papCampaignId: string;
  campaignName: string | null;
  isVip?: boolean;
  active: boolean;
  registrations: number;
  ftd_count: number;
  deposits_volume: number;
  ngr_volume: number;
  commission: number;
  commission_rs: number;
  commission_cpa: number;
  firstDay: string | null;
  lastDay: string | null;
}
export interface KlientMonth {
  ym: string;
  affiliates: number;
  clicks: number;
  registrations: number;
  ftd_count: number;
  deposits_count: number;
  deposits_volume: number;
  ngr_volume: number;
  commission: number;
  commission_rs: number;
  commission_cpa: number;
  withdrawals_count: number;
}
export interface KlientDetail extends KlientListItem {
  ic: string | null;
  vat: string | null;
  uolCompanyName: string | null;
  uolDic: string | null;
  uolCountry: string | null;
  uolContactId: string | null;
  uolCheckedAt: string | null;
  billingEmails?: string | null;
  platform?: string | null;
  affiliatePlatform?: string | null;
  campaignList: KlientCampaign[];
  months: KlientMonth[];
  affiliatesCount: number;
  vipBilling?: boolean;
  vipInstances?: Array<{ code: string; casino: string; accounts: number }>;
  monthsByView?: { affilmaster: KlientMonth[]; vip: KlientMonth[] } | null;
}
export const setAffilmasterKlientVipBilling = (partnerId: number, enabled: boolean) =>
  afPost<{ enabled: boolean; campaigns: number }>(`/api/v1/affilmaster/klienti/${partnerId}/vip-billing`, { enabled });
export async function getAffilmasterKlienti(): Promise<KlientListItem[]> {
  const { body } = await getRaw('/api/v1/affilmaster/klienti');
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'klienti vrátil non-ok');
  return body.data as KlientListItem[];
}
export async function getAffilmasterKlientDetail(id: number): Promise<KlientDetail> {
  const { body } = await getRaw(`/api/v1/affilmaster/klienti/${id}`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'klient detail vrátil non-ok');
  return body.data as KlientDetail;
}

export interface PartnerMapRow {
  id: number; name: string; ic: string | null; vat: string | null; market: string | null; billingCurrency: string | null; active: number;
  website: string | null; logo: string | null; logoBg: string | null;
  uolCompanyName: string | null; uolDic: string | null; uolCountry: string | null; uolContactId: string | null; uolCheckedAt: string | null;
  campaigns: Array<{ id: number; papCampaignId: string; campaignName: string | null; active: number }>;
}
export async function getAffilmasterPartnersMap(): Promise<PartnerMapRow[]> {
  const { body } = await getRaw('/api/v1/affilmaster/partners-map');
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'partners-map vrátil non-ok');
  return body.data as PartnerMapRow[];
}

export interface PapCampaignPick { id: string; name: string | null; status: string | null; type: string | null }
export async function getAffilmasterPapCampaigns(): Promise<PapCampaignPick[]> {
  const { body } = await getRaw('/api/v1/affilmaster/pap-campaigns');
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'pap-campaigns vrátil non-ok');
  return body.data as PapCampaignPick[];
}
async function afPost<T = unknown>(path: string, payload?: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: payload != null ? JSON.stringify(payload) : undefined,
  });
  let body: any = null;
  try { body = await res.json(); } catch { /* empty */ }
  if (!res.ok || !body?.ok) {
    throw new AffilmasterApiError(res.status, body?.error?.code ?? body?.message ?? `HTTP_${res.status}`, body?.error?.message ?? body?.message);
  }
  return body.data as T;
}
export const createAffilmasterPartner = (p: { name: string; ic?: string; vat?: string; market?: string; billingCurrency?: string }) =>
  afPost<{ id: number }>('/api/v1/affilmaster/partners', p);
export const updateAffilmasterPartner = (id: number, p: { name?: string; ic?: string; vat?: string; market?: string; billingCurrency?: string; website?: string; platform?: string; affiliatePlatform?: string; isExclusive?: boolean; billingEmails?: string }) =>
  afPost(`/api/v1/affilmaster/partners/${id}/update`, p);
export async function getAffilmasterKlientDocuments(id: number): Promise<DriveDocuments> {
  const { body } = await getRaw(`/api/v1/affilmaster/klienti/${id}/documents`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', body?.message ?? 'Načtení dokumentů selhalo');
  return body.data as DriveDocuments;
}
export const refreshAffilmasterKlientDocuments = (partnerId: number) =>
  afPost<DriveDocuments>(`/api/v1/affilmaster/klienti/${partnerId}/documents/refresh`);
export const setAffilmasterKlientDriveFolder = (partnerId: number, url: string | null) =>
  afPost(`/api/v1/affilmaster/partners/${partnerId}/drive-folder`, { url: url ?? '' });
export const pairAffilmasterCampaign = (partnerId: number, papCampaignId: string, campaignName?: string | null) =>
  afPost(`/api/v1/affilmaster/partners/${partnerId}/campaigns`, { papCampaignId, campaignName });
export const unpairAffilmasterCampaign = (partnerId: number, rowId: number) =>
  afPost(`/api/v1/affilmaster/partners/${partnerId}/campaigns/${rowId}/deactivate`);
export const uploadAffilmasterLogo = (id: number, contentBase64: string, mime: string, logoBg: string | null) =>
  afPost<{ logo: string; logoBg: string | null }>(`/api/v1/affilmaster/partners/${id}/logo`, { contentBase64, mime, logoBg });

export interface KlientContact { id: number; name: string; email: string | null; phone: string | null; position: string | null; vocative: string | null; invoiceCc?: boolean }
export async function getAffilmasterKlientContacts(id: number): Promise<KlientContact[]> {
  const { body } = await getRaw(`/api/v1/affilmaster/klienti/${id}/contacts`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'contacts vrátil non-ok');
  return body.data as KlientContact[];
}
export interface KlientInvoice {
  invoiceNumber: string | null;
  issuedDate: string | null;
  dueDate: string | null;
  totalAmount: number | null;
  paidAmount: number | null;
  currency: string | null;
  docType: string | null;
  paid: boolean;
}
export async function getAffilmasterKlientInvoices(id: number): Promise<KlientInvoice[]> {
  const { body } = await getRaw(`/api/v1/affilmaster/klienti/${id}/invoices`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'invoices vrátil non-ok');
  return body.data as KlientInvoice[];
}

export function affilmasterKlientInvoicePdfUrl(klientId: number, invoiceNumber: string): string {
  return `/api/v1/affilmaster/klienti/${klientId}/invoices/${encodeURIComponent(invoiceNumber)}/pdf`;
}

export interface KlientNote {
  id: number;
  body: string;
  source: string;
  notedAt: string | null;
  createdBy: string | null;
}
export async function getAffilmasterKlientNotes(id: number): Promise<KlientNote[]> {
  const { body } = await getRaw(`/api/v1/affilmaster/klienti/${id}/notes`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'notes vrátil non-ok');
  return body.data as KlientNote[];
}
export const addAffilmasterKlientNote = (partnerId: number, text: string) =>
  afPost<{ id: number }>(`/api/v1/affilmaster/partners/${partnerId}/notes`, { body: text });
export const updateAffilmasterKlientNote = (partnerId: number, noteId: number, text: string) =>
  afPost(`/api/v1/affilmaster/partners/${partnerId}/notes/${noteId}/update`, { body: text });
export const deleteAffilmasterKlientNote = (partnerId: number, noteId: number) =>
  afPost(`/api/v1/affilmaster/partners/${partnerId}/notes/${noteId}/delete`);

export interface AffiliateInvoice {
  invoiceNumber: string | null;
  variableSymbol: string | null;
  issuedDate: string | null;
  dueDate: string | null;
  totalAmount: number | null;
  currency: string | null;
}
export async function getAffilmasterAffiliateInvoices(papId: string): Promise<AffiliateInvoice[]> {
  const { body } = await getRaw(`/api/v1/affilmaster/affiliates/${encodeURIComponent(papId)}/invoices`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'invoices vrátil non-ok');
  return body.data as AffiliateInvoice[];
}

export interface KlientContactInput { name?: string; email?: string; phone?: string; position?: string; vocative?: string; invoiceCc?: boolean }
export const addAffilmasterKlientContact = (partnerId: number, p: KlientContactInput) =>
  afPost<{ id: number }>(`/api/v1/affilmaster/partners/${partnerId}/contacts`, p);
export const updateAffilmasterKlientContact = (partnerId: number, contactId: number, p: KlientContactInput) =>
  afPost(`/api/v1/affilmaster/partners/${partnerId}/contacts/${contactId}/update`, p);
export const deleteAffilmasterKlientContact = (partnerId: number, contactId: number) =>
  afPost(`/api/v1/affilmaster/partners/${partnerId}/contacts/${contactId}/deactivate`);

export type { KlientDeal, KlientDealInput, KlientDealInvoiceGroup, KlientDealKind, KlientDealStatus } from './klienti';
import type { KlientDeal, KlientDealInput, KlientDealStatus } from './klienti';
export async function getAffilmasterKlientDeals(id: number): Promise<KlientDeal[]> {
  const { body } = await getRaw(`/api/v1/affilmaster/klienti/${id}/deals`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'deals vrátil non-ok');
  return body.data as KlientDeal[];
}
export const addAffilmasterKlientDeal = (partnerId: number, p: KlientDealInput) =>
  afPost<{ id: number }>(`/api/v1/affilmaster/partners/${partnerId}/deals`, p);
export const updateAffilmasterKlientDeal = (partnerId: number, dealId: number, p: KlientDealInput) =>
  afPost(`/api/v1/affilmaster/partners/${partnerId}/deals/${dealId}/update`, p);
export const setAffilmasterKlientDealStatus = (partnerId: number, dealId: number, status: KlientDealStatus) =>
  afPost(`/api/v1/affilmaster/partners/${partnerId}/deals/${dealId}/status`, { status });

export interface UolCheckResult { matched: boolean; contactId?: string; name?: string | null; dic?: string | null; country?: string | null }
export const uolCheckAffilmasterKlient = (partnerId: number) =>
  afPost<UolCheckResult>(`/api/v1/affilmaster/partners/${partnerId}/uol-check`);
export interface KlientAffiliateRow {
  pap_affiliate_id: string;
  name: string;
  campaigns: number;
  registrations: number;
  ftd_count: number;
  deposits_volume: number;
  ngr_volume: number;
  commission: number;
  commission_rs: number;
  commission_cpa: number;
}
export async function getAffilmasterKlientAffiliates(id: number): Promise<{ currency: string; affiliates: KlientAffiliateRow[] }> {
  const { body } = await getRaw(`/api/v1/affilmaster/klienti/${id}/affiliates`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'klient affiliates non-ok');
  return body.data as { currency: string; affiliates: KlientAffiliateRow[] };
}
export interface KlientMonthAffiliateRow {
  pap_affiliate_id: string; name: string; campaigns: number;
  registrations: number; ftd_count: number; deposits_volume: number; ngr_volume: number;
  commission: number; commission_rs: number; commission_cpa: number;
}
export interface KlientMonthData {
  currency: string; ym: string;
  totals: { affiliates: number; campaigns: number; registrations: number; ftd_count: number; deposits_volume: number; ngr_volume: number; commission: number; commission_rs: number; commission_cpa: number };
  affiliates: KlientMonthAffiliateRow[];
}
export async function getAffilmasterKlientMonthAffiliates(id: number, ym: string): Promise<KlientMonthData> {
  const { body } = await getRaw(`/api/v1/affilmaster/klienti/${id}/month/${encodeURIComponent(ym)}`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'klient month non-ok');
  return body.data as KlientMonthData;
}
export async function getAffilmasterKlientCampaignChart(id: number, campaignId: string): Promise<AffiliateCampaignChart> {
  const { body } = await getRaw(`/api/v1/affilmaster/klienti/${id}/campaign/${encodeURIComponent(campaignId)}/chart`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'klient campaign chart non-ok');
  return body.data as AffiliateCampaignChart;
}

export interface DashboardMarketMetrics {
  registrations: number; ftd_count: number; deposits_count: number; withdrawals_count: number;
  deposits_volume: number; ngr_volume: number; commission: number;
}
export interface DashboardMarketBlock {
  month: DashboardMarketMetrics & { affiliates_active: number; new_affiliates: number };
  total: DashboardMarketMetrics & { affiliates_total: number };
}
export interface DashboardFinanceMonth {
  registrations: number; ftd_count: number; deposits_volume: number; ngr_volume: number; commission: number;
}
export interface AffilmasterDashboard {
  ym: string;
  rate: number;
  labels: string[];
  cz: DashboardMarketBlock;
  sk: DashboardMarketBlock;
  activity: { active: number; inactive: number; total: number };
  trend: { labels: string[]; cz: number[]; sk: number[] };
  financeTrend: { labels: string[]; cz: DashboardFinanceMonth[]; sk: DashboardFinanceMonth[] };
}
export type VipMode = 'all' | 'without' | 'only';
export interface VipCompareMetrics { registrations: number; ftd_count: number; deposits_volume: number; ngr_volume: number; commission: number }
export interface VipCompareSplit { all: VipCompareMetrics; without: VipCompareMetrics; only: VipCompareMetrics }
export interface VipComparePartner {
  papAffiliateId: string; name: string | null;
  instances: Array<{ code: string; casino: string; currency: 'CZK' | 'EUR'; month: VipCompareMetrics }>;
}
export interface VipSharePartner {
  papAffiliateId: string; name: string | null;
  month: { cz: VipCompareMetrics; sk: VipCompareMetrics };
  total: { cz: VipCompareMetrics; sk: VipCompareMetrics };
}
export interface VipComparison {
  ym: string; rate: number;
  month: { cz: VipCompareSplit; sk: VipCompareSplit };
  total: { cz: VipCompareSplit; sk: VipCompareSplit };
  months: Array<{ ym: string; cz: VipCompareSplit; sk: VipCompareSplit }>;
  vipPartners: VipSharePartner[];
}
export interface VipMonthBreakdown { ym: string; rate: number; partners: VipComparePartner[] }
export async function getAffilmasterVipMonthBreakdown(ym: string): Promise<VipMonthBreakdown> {
  const { body } = await getRaw(`/api/v1/affilmaster/vip-comparison/month?ym=${encodeURIComponent(ym)}`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'vip month non-ok');
  return body.data as VipMonthBreakdown;
}
export async function getAffilmasterVipComparison(ym?: string): Promise<VipComparison> {
  const q = ym ? `?ym=${encodeURIComponent(ym)}` : '';
  const { body } = await getRaw(`/api/v1/affilmaster/vip-comparison${q}`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'vip comparison non-ok');
  return body.data as VipComparison;
}
export async function getAffilmasterDashboard(ym?: string, vip: VipMode = 'all'): Promise<AffilmasterDashboard> {
  const sp = new URLSearchParams();
  if (ym) sp.set('ym', ym);
  if (vip !== 'all') sp.set('vip', vip);
  const q = sp.toString() ? `?${sp.toString()}` : '';
  const { body } = await getRaw(`/api/v1/affilmaster/dashboard${q}`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'dashboard non-ok');
  return body.data as AffilmasterDashboard;
}

export interface AffFinancePeriodMetrics {
  registrations: number; deposits_volume: number; ngr_volume: number; commission: number;
}
export interface AffFinanceTimeseries {
  granularity: 'month' | 'year';
  rate: number;
  rows: {
    period: string;
    cz: AffFinancePeriodMetrics;
    sk: AffFinancePeriodMetrics;
    partial?: boolean;
    prevSamePeriod?: { cz: AffFinancePeriodMetrics; sk: AffFinancePeriodMetrics };
  }[];
}
export async function getAffilmasterFinanceTimeseries(granularity: 'month' | 'year', vip: VipMode = 'all'): Promise<AffFinanceTimeseries> {
  const { body } = await getRaw(`/api/v1/affilmaster/finance-timeseries?granularity=${granularity}${vip !== 'all' ? `&vip=${vip}` : ''}`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'finance-timeseries non-ok');
  return body.data as AffFinanceTimeseries;
}

export interface AffFinanceMonthAffiliate {
  pap_affiliate_id: string;
  display_name: string;
  vip_partner?: boolean;
  registrations: number;
  deposits_volume: number;
  ngr_volume: number;
  commission: number;
}
export interface AffFinanceMonthDetail {
  ym: string;
  market: 'CZ' | 'SK';
  currency: 'CZK' | 'EUR';
  rate: number;
  affiliates: AffFinanceMonthAffiliate[];
}
export async function getAffilmasterFinanceMonthDetail(ym: string, market: 'CZ' | 'SK', vip: VipMode = 'all'): Promise<AffFinanceMonthDetail> {
  const { body } = await getRaw(`/api/v1/affilmaster/finance-timeseries/month?ym=${encodeURIComponent(ym)}&market=${market}${vip !== 'all' ? `&vip=${vip}` : ''}`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'finance-timeseries month detail non-ok');
  return body.data as AffFinanceMonthDetail;
}

export async function getAffilmasterFreshness(): Promise<Readiness> {
  const { body } = await getRaw('/api/v1/affilmaster/freshness');
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'freshness vrátil non-ok');
  return body.data as Readiness;
}

function toReporting<T>(body: any): Reporting<T> {
  if (body?.ok === true) return { ok: true, data: body.data as T, freshness: body.freshness as Readiness };
  return { ok: false, notReady: true, freshness: (body?.freshness ?? { ready: false, problems: ['neznámý stav'], thresholds: { maxAgeHours: 0, maxDataLagDays: 0 }, sources: [] }) as Readiness };
}

export async function getAffilmasterAffiliates(days?: number): Promise<Reporting<AffiliateListItem[]>> {
  const q = days != null ? `?days=${encodeURIComponent(String(days))}` : '';
  const { body } = await getRaw(`/api/v1/affilmaster/affiliates${q}`);
  return toReporting<AffiliateListItem[]>(body);
}

export async function getAffilmasterAffiliateDetail(papId: string): Promise<Reporting<AffiliateMonthly>> {
  const { body } = await getRaw(`/api/v1/affilmaster/affiliates/${encodeURIComponent(papId)}`);
  return toReporting<AffiliateMonthly>(body);
}

export interface AffiliateCampaignChartDay {
  day: string;
  registrations: number;
  ftd_count: number;
  deposits_count: number;
  deposits_volume: number;
  ngr_volume: number;
  commission: number;
}
export interface AffiliateCampaignChart {
  currency: string;
  campaign_name: string | null;
  daily: AffiliateCampaignChartDay[];
}
export interface AffiliateMonthBreakdown {
  currency: string;
  ym: string;
  campaigns: AffiliateCampaign[];
}
export async function getAffilmasterAffiliateCampaignChart(papId: string, campaignId: string): Promise<AffiliateCampaignChart> {
  const { body } = await getRaw(`/api/v1/affilmaster/affiliates/${encodeURIComponent(papId)}/campaign/${encodeURIComponent(campaignId)}/chart`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'campaign chart non-ok');
  return body.data as AffiliateCampaignChart;
}
export async function getAffilmasterAffiliateMonthBreakdown(papId: string, ym: string): Promise<AffiliateMonthBreakdown> {
  const { body } = await getRaw(`/api/v1/affilmaster/affiliates/${encodeURIComponent(papId)}/month/${encodeURIComponent(ym)}`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'month breakdown non-ok');
  return body.data as AffiliateMonthBreakdown;
}

export interface AffiliateContact {
  id: number;
  name: string;
  vocative: string | null;
  role: string | null;
  email: string | null;
  phone: string | null;
  note: string | null;
  created_at: string | null;
}
export interface NewAffiliateContact {
  name: string;
  vocative?: string | null;
  role?: string | null;
  email?: string | null;
  phone?: string | null;
  note?: string | null;
}

export interface NewAffiliate {
  name: string;
  website?: string | null;
  markets: string[];
  country?: string | null;
  email?: string | null;
  phone?: string | null;
}

export async function addAffilmasterAffiliate(data: NewAffiliate): Promise<{ pap_affiliate_id: string }> {
  const res = await fetch('/api/v1/affilmaster/affiliates', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  let body: any = null;
  try { body = await res.json(); } catch { /* empty */ }
  if (!res.ok || !body?.ok) {
    throw new AffilmasterApiError(res.status, body?.error?.code ?? body?.message ?? `HTTP_${res.status}`, body?.error?.message ?? body?.message);
  }
  return body.data as { pap_affiliate_id: string };
}

export async function getAffilmasterContacts(papId: string): Promise<AffiliateContact[]> {
  const { body } = await getRaw(`/api/v1/affilmaster/affiliates/${encodeURIComponent(papId)}/contacts`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'contacts non-ok');
  return (body.data ?? []) as AffiliateContact[];
}

export type AffiliateNote = KlientNote;

export async function getAffilmasterAffiliateNotes(papId: string): Promise<AffiliateNote[]> {
  const { body } = await getRaw(`/api/v1/affilmaster/affiliates/${encodeURIComponent(papId)}/notes`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'affiliate notes non-ok');
  return (body.data ?? []) as AffiliateNote[];
}

export function addAffilmasterAffiliateNote(papId: string, text: string): Promise<{ id: number }> {
  return afPost(`/api/v1/affilmaster/affiliates/${encodeURIComponent(papId)}/notes`, { body: text });
}

export async function updateAffilmasterAffiliateNote(papId: string, noteId: number, text: string): Promise<void> {
  const res = await fetch(`/api/v1/affilmaster/affiliates/${encodeURIComponent(papId)}/notes/${noteId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body: text }),
  });
  if (!res.ok) {
    let body: any = null;
    try { body = await res.json(); } catch { body = null; }
    throw new AffilmasterApiError(res.status, body?.error?.code ?? `HTTP_${res.status}`, body?.error?.message ?? body?.message);
  }
}

export async function deleteAffilmasterAffiliateNote(papId: string, noteId: number): Promise<void> {
  const res = await fetch(`/api/v1/affilmaster/affiliates/${encodeURIComponent(papId)}/notes/${noteId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    let body: any = null;
    try { body = await res.json(); } catch { body = null; }
    throw new AffilmasterApiError(res.status, body?.error?.code ?? `HTTP_${res.status}`, body?.error?.message ?? body?.message);
  }
}

export async function addAffilmasterContact(papId: string, contact: NewAffiliateContact): Promise<{ id: number }> {
  const res = await fetch(`/api/v1/affilmaster/affiliates/${encodeURIComponent(papId)}/contacts`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(contact),
  });
  let body: any = null;
  try { body = await res.json(); } catch { /* empty */ }
  if (!res.ok || !body?.ok) {
    throw new AffilmasterApiError(res.status, body?.error?.code ?? body?.message ?? `HTTP_${res.status}`, body?.error?.message ?? body?.message);
  }
  return body.data as { id: number };
}

export async function updateAffilmasterContact(papId: string, contactId: number, contact: NewAffiliateContact): Promise<void> {
  const res = await fetch(`/api/v1/affilmaster/affiliates/${encodeURIComponent(papId)}/contacts/${contactId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(contact),
  });
  if (!res.ok) {
    let body: any = null;
    try { body = await res.json(); } catch { /* empty */ }
    throw new AffilmasterApiError(res.status, body?.error?.code ?? `HTTP_${res.status}`, body?.error?.message ?? body?.message);
  }
}

export async function deleteAffilmasterContact(papId: string, contactId: number): Promise<void> {
  const res = await fetch(`/api/v1/affilmaster/affiliates/${encodeURIComponent(papId)}/contacts/${contactId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    let body: any = null;
    try { body = await res.json(); } catch { /* empty */ }
    throw new AffilmasterApiError(res.status, body?.error?.code ?? `HTTP_${res.status}`, body?.error?.message ?? body?.message);
  }
}

export async function updateAffilmasterPrimaryContact(papId: string, contact: NewAffiliateContact): Promise<void> {
  await afPost(`/api/v1/affilmaster/affiliates/${encodeURIComponent(papId)}/primary-contact`, contact);
}
export async function updateAffilmasterAffiliateProfile(papId: string, profile: {
  displayName:string; companyName:string; website:string; country:string; markets:string; status:string;
  ico:string; dic:string; billingStreet:string; billingCity:string; billingZip:string; billingCountry:string;
}): Promise<void> {
  await afPost(`/api/v1/affilmaster/affiliates/${encodeURIComponent(papId)}/profile`, profile);
}
export function pairAffilmasterAffiliateWithPap(manualId: string, papId: string): Promise<{ papAffiliateId: string; displayName: string }> {
  return afPost(`/api/v1/affilmaster/affiliates/${encodeURIComponent(manualId)}/pair-pap`, { papId });
}
export async function deleteAffilmasterAffiliate(papId: string): Promise<void> {
  await afPost(`/api/v1/affilmaster/affiliates/${encodeURIComponent(papId)}/delete`, {});
}

export interface KlientPrehledClient { id: number; name: string; currency: string; market: string }
export async function getKlientiPrehledList(market: 'CZ' | 'SK'): Promise<KlientPrehledClient[]> {
  const { body } = await getRaw(`/api/v1/affilmaster/klienti-prehled/list?market=${market}`);
  return (body?.data ?? []) as KlientPrehledClient[];
}

export interface KlientDashboardTile {
  id: number; name: string; market: string; currency: string;
  registrations: number; ftd_count: number; deposits_volume: number; ngr_volume: number;
  altCurrency: string | null; altDeposits: number | null; altNgr: number | null;
}
export interface KlientDashboard { ym: string; clients: KlientDashboardTile[] }
export async function getKlientiPrehledDashboard(ym?: string): Promise<KlientDashboard> {
  const qs = ym ? `?ym=${encodeURIComponent(ym)}` : '';
  const { body } = await getRaw(`/api/v1/affilmaster/klienti-prehled/dashboard${qs}`);
  return (body?.data ?? { ym: '', clients: [] }) as KlientDashboard;
}

export type KlientPrehledMetric = 'clicks' | 'registrations' | 'ftd_count' | 'ngr_volume' | 'commission';
export interface KlientPrehledSeries { partnerId: number; name: string; metric: string; values: number[] }
export interface KlientPrehledTimeseries { currency: string; months: string[]; series: KlientPrehledSeries[] }
export async function getKlientiPrehledTimeseries(args: {
  market: 'CZ' | 'SK'; partnerIds: number[]; metrics: KlientPrehledMetric[]; ym: string | 'all';
}): Promise<KlientPrehledTimeseries> {
  const qs = new URLSearchParams({
    market: args.market,
    partnerIds: args.partnerIds.join(','),
    metrics: args.metrics.join(','),
    ym: args.ym,
  }).toString();
  const { body } = await getRaw(`/api/v1/affilmaster/klienti-prehled/timeseries?${qs}`);
  return (body?.data ?? { currency: 'CZK', months: [], series: [] }) as KlientPrehledTimeseries;
}

export interface AresRecord {
  ico: string; dic: string | null; companyName: string | null;
  street: string | null; city: string | null; zip: string | null; country: string;
}
export async function lookupAresByIco(ico: string): Promise<AresRecord> {
  const { status, body } = await getRaw(`/api/v1/affilmaster/ares/${encodeURIComponent(ico)}`);
  if (!body || typeof body !== 'object') throw new AffilmasterApiError(status, 'ARES_INVALID', 'ARES: nevalidní odpověď');
  const b = body as { ok?: boolean; data?: AresRecord; error?: { code?: string; message?: string } };
  if (!b.ok || !b.data) throw new AffilmasterApiError(status, b.error?.code ?? 'ARES_ERROR', b.error?.message ?? 'ARES: nenalezeno');
  return b.data;
}

let mailingGroupsBase = '/api/v1/affilmaster/mailing';
export function setMailingGroupsSpace(space: 'affilmaster' | 'affilplatform') {
  mailingGroupsBase = `/api/v1/${space}/mailing`;
}

export interface MailingSegmentRule {
  metric: string;
  op: string;
  value: string | number;
}
export interface MailingSegmentItem {
  id: number;
  name: string;
  color: string;
  country: string;
  contactCount: number;
  ruleCount: number;
  ruleSummary: string | null;
  rules: MailingSegmentRule[];
  priority: number;
  isActive: boolean;
  inList: boolean;
  missingVocative?: number;
}
export interface MailingSegmentInput {
  name: string;
  country: string;
  color: string;
  priority: number;
  active: boolean;
  inList: boolean;
  rules: MailingSegmentRule[];
}

export async function listMailingSegments(q: { search?: string; country?: string; stav?: string }): Promise<{ items: MailingSegmentItem[]; total: number }> {
  const p = new URLSearchParams();
  if (q.search) p.set('search', q.search);
  if (q.country) p.set('country', q.country);
  if (q.stav) p.set('stav', q.stav);
  const qs = p.toString();
  const { body } = await getRaw(`${mailingGroupsBase}/segments${qs ? `?${qs}` : ''}`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'segments non-ok');
  return body.data as { items: MailingSegmentItem[]; total: number };
}

export async function updateMailingContactVocative(input: { source: string; sourceId: string; vocative: string }): Promise<{ vocative: string }> {
  return afPost<{ vocative: string }>(`${mailingGroupsBase}/contacts/vocative`, input);
}
export async function excludeFromMailingSegment(id: number, email: string): Promise<{ excluded: string }> {
  return afPost<{ excluded: string }>(`${mailingGroupsBase}/segments/${id}/exclude`, { email });
}
export async function includeInMailingSegment(id: number, email: string): Promise<{ included: string }> {
  return afPost<{ included: string }>(`${mailingGroupsBase}/segments/${id}/include`, { email });
}
export async function createMailingSegment(input: MailingSegmentInput): Promise<{ id: number }> {
  return afPost<{ id: number }>(`${mailingGroupsBase}/segments`, input);
}
export async function updateMailingSegment(id: number, input: MailingSegmentInput): Promise<{ updated: boolean }> {
  const res = await fetch(`${mailingGroupsBase}/segments/${id}`, {
    method: 'PUT', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  let body: any = null;
  try { body = await res.json(); } catch { /* empty */ }
  if (!res.ok || !body?.ok) throw new AffilmasterApiError(res.status, body?.error?.code ?? `HTTP_${res.status}`, body?.error?.message);
  return body.data as { updated: boolean };
}
export async function deleteMailingSegment(id: number): Promise<{ deleted: boolean }> {
  const res = await fetch(`${mailingGroupsBase}/segments/${id}`, { method: 'DELETE', credentials: 'include' });
  let body: any = null;
  try { body = await res.json(); } catch { /* empty */ }
  if (!res.ok || !body?.ok) throw new AffilmasterApiError(res.status, body?.error?.code ?? `HTTP_${res.status}`, body?.error?.message);
  return body.data as { deleted: boolean };
}
export async function rebuildMailingContacts(): Promise<{ inserted: number; skipped: number; segmentsRecomputed: number }> {
  return afPost<{ inserted: number; skipped: number; segmentsRecomputed: number }>(`${mailingGroupsBase}/contacts/rebuild`);
}

export interface MailingContactsStatus {
  total: number;
  lastUpdated: string | null;
  perSourceCountry: Array<{ source: string; country: string | null; n: number }>;
}
export async function getMailingContactsStatus(): Promise<MailingContactsStatus> {
  const { body } = await getRaw(`${mailingGroupsBase}/contacts/status`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'status non-ok');
  return body.data as MailingContactsStatus;
}

export interface MailingContactRow {
  id: string;
  source: 'affiliate' | 'inzerent' | 'manual';
  sourceId: string;
  parentId: string;
  parentName: string | null;
  country: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  position: string | null;
  vocative?: string | null;
  note: string | null;
}
export interface MailingSegmentContactsResp {
  segmentId: number;
  name: string;
  country: string;
  rules: MailingSegmentRule[];
  rows: Array<MailingContactRow & { vocativeOk: boolean }>;
  missingVocative: number;
  excluded: MailingExcludedContact[];
}
export interface MailingExcludedContact {
  email: string;
  name: string | null;
  parentName: string | null;
  source: 'affiliate' | 'inzerent' | 'manual' | null;
  sourceId: string | null;
}
export interface MailingManualGroup {
  id: number;
  name: string;
  color: string;
  country: string;
  member: boolean;
}
type MailingGroupsSpace = 'affilmaster' | 'affilplatform';
const groupsBaseFor = (space?: MailingGroupsSpace) => (space ? `/api/v1/${space}/mailing` : mailingGroupsBase);

export async function listMailingManualGroups(email: string, space?: MailingGroupsSpace): Promise<MailingManualGroup[]> {
  const { body } = await getRaw(`${groupsBaseFor(space)}/contacts/manual-groups?email=${encodeURIComponent(email)}`);
  if (!body?.ok) throw new AffilmasterApiError(200, body?.error?.code ?? 'INVALID_RESPONSE', body?.error?.message ?? 'Načtení skupin selhalo');
  return body.data as MailingManualGroup[];
}
export async function setMailingGroupMember(id: number, email: string, member: boolean, space?: MailingGroupsSpace): Promise<{ email: string; member: boolean }> {
  return afPost<{ email: string; member: boolean }>(`${groupsBaseFor(space)}/segments/${id}/members`, { email, member });
}
export async function getMailingSegmentContacts(id: number): Promise<MailingSegmentContactsResp> {
  const { body } = await getRaw(`${mailingGroupsBase}/segments/${id}/contacts`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'segment contacts non-ok');
  return body.data as MailingSegmentContactsResp;
}

export interface BankaUcet {
  id: number;
  key: string;
  name: string;
  currency: string;
  accountNo: string | null;
  bankCode: string | null;
  iban: string | null;
  closingBalance: number | null;
  balanceDate: string | null;
  balanceSyncedAt: string | null;
  lastSyncAt: string | null;
  lastStatus: string | null;
}
export async function getAffilmasterBankaUcty(): Promise<BankaUcet[]> {
  const { body } = await getRaw('/api/v1/affilmaster/banka/ucty');
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'banka/ucty non-ok');
  return body.data.accounts as BankaUcet[];
}

export interface FinanceDashboard {
  balances: {
    totalCzk: number;
    rate: number;
    asOf: string | null;
    accountsWithoutData: number;
    byCurrency: { currency: string; amount: number; accounts: number }[];
    accounts: { id: number; name: string; currency: string; accountNo: string | null; bankCode: string | null; amount: number | null; balanceDate: string | null }[];
  };
  unpaidInvoices: {
    totalCzk: number;
    rate: number;
    byCurrency: { currency: string; count: number; amount: number; overdueCount: number; overdueAmount: number }[];
    refreshedAt: string | null;
  };
  settlement: SettlementForecast;
}
export interface SettlementForecastRow {
  partnerId: number; name: string; market: 'cz' | 'sk';
  fixed: number; fixedCurrency: string; fixedItems: { title: string; amount: number; currency: string; invoiceGroup: string }[];
  rs: number; cpa: number; reg: number; feeCurrency: string; feesTotal: number;
  totalCzk: number; feeBasis: string[]; hasDeals: boolean;
}
export interface MarketIncome { fixedCzk: number; feesCzk: number; clients: number; totalCzk: number }
export interface SettlementForecast {
  ym: string; rate: number;
  fixed: { czk: number; eur: number; totalCzk: number };
  fees: { rsCzk: number; rsEur: number; cpaCzk: number; cpaEur: number; regCzk: number; regEur: number; totalCzk: number };
  income: { czk: number; eur: number; totalCzk: number };
  markets: { cz: MarketIncome; sk: MarketIncome };
  partners: SettlementForecastRow[];
}
export interface IncomeSeriesRow {
  period: string; partial: boolean;
  czk: number; eur: number; totalCzk: number; fixedCzk: number; feesCzk: number;
  cz: MarketIncome; sk: MarketIncome;
}
export interface IncomeSeries { ym: string; rate: number; rows: IncomeSeriesRow[] }
export async function getAffilmasterPrijmy(months = 24): Promise<IncomeSeries> {
  const { body } = await getRaw(`/api/v1/affilmaster/finance/prijmy?months=${months}`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'finance/prijmy non-ok');
  return body.data as IncomeSeries;
}
export async function getAffilmasterPrijmyMonth(ym: string): Promise<SettlementForecast> {
  const { body } = await getRaw(`/api/v1/affilmaster/finance/prijmy/month?ym=${encodeURIComponent(ym)}`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'finance/prijmy/month non-ok');
  return body.data as SettlementForecast;
}
export async function getAffilmasterFinanceDashboard(): Promise<FinanceDashboard> {
  const { body } = await getRaw('/api/v1/affilmaster/finance/dashboard');
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'finance/dashboard non-ok');
  return body.data as FinanceDashboard;
}


export type SocialPlatform = 'youtube' | 'tiktok' | 'instagram' | 'facebook' | 'twitch' | 'kick' | 'telegram' | 'twitter' | 'web';
export interface SocialMetric {
  key: string;
  label: string;
  value: number | string | boolean | null;
}
export interface SocialItem {
  externalId: string | null;
  title: string;
  text: string | null;
  url: string | null;
  publishedAt: string | null;
  views: number | null;
  likes: number | null;
  comments: number | null;
  extra?: Record<string, string | number | null>;
}
export interface SocialKeywordRank {
  keyword: string;
  country: string;
  position: number | null;
  url: string | null;
  checkedTop: number;
}
export interface SocialProbeResult {
  platform: SocialPlatform;
  inputUrl: string;
  handle: string | null;
  status: 'ok' | 'error' | 'unconfigured' | 'unsupported';
  source: string;
  fetchedAt: string;
  durationMs: number;
  profile: Record<string, unknown> | null;
  metrics: SocialMetric[];
  items: SocialItem[];
  keywords?: SocialKeywordRank[];
  error: string | null;
}
export async function postAffilmasterSocialsTest(urls: string[]): Promise<SocialProbeResult[]> {
  const res = await fetch('/api/v1/affilmaster/socials/test', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ urls }),
  });
  let body: any = null;
  try { body = await res.json(); } catch { /* empty */ }
  if (!res.ok) {
    const msg = Array.isArray(body?.message) ? body.message.join(', ') : (body?.error?.message ?? body?.message);
    throw new AffilmasterApiError(res.status, body?.error?.code ?? `HTTP_${res.status}`, msg);
  }
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'socials/test non-ok');
  return body.data.results as SocialProbeResult[];
}

export interface AffiliateSocialProfile {
  id: number;
  platform: SocialPlatform;
  url: string;
  handle: string | null;
  kind: string | null;
  title: string | null;
  faviconUrl: string | null;
  avatarUrl: string | null;
  lastCollectedAt: string | null;
  lastStatus: string | null;
  lastError: string | null;
  stale: boolean;
  snapshotDate: string | null;
  primary: { key: string; label: string; value: number | null };
  metrics: SocialMetric[];
  monthly: { ym: string; value: number | null }[];
  posts: SocialItem[];
  keywords: SocialKeywordRank[];
  profile: Record<string, unknown> | null;
}
export interface AffiliateSocials {
  affiliateId: string;
  profiles: AffiliateSocialProfile[];
  stale: boolean;
  collectedAt: string | null;
}
export async function getAffiliateSocials(papId: string): Promise<AffiliateSocials> {
  const { body } = await getRaw(`/api/v1/affilmaster/socials/affiliates/${encodeURIComponent(papId)}`);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'socials/affiliates non-ok');
  return body.data as AffiliateSocials;
}
export async function refreshAffiliateSocials(papId: string, force = false): Promise<AffiliateSocials> {
  const res = await fetch(`/api/v1/affilmaster/socials/affiliates/${encodeURIComponent(papId)}/refresh${force ? '?force=1' : ''}`, { method: 'POST', credentials: 'include' });
  let body: any = null;
  try { body = await res.json(); } catch { /* empty */ }
  if (!res.ok) throw new AffilmasterApiError(res.status, body?.error?.code ?? `HTTP_${res.status}`, body?.error?.message ?? body?.message);
  if (!body?.ok) throw new AffilmasterApiError(200, 'INVALID_RESPONSE', 'socials/refresh non-ok');
  return body.data as AffiliateSocials;
}
